import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Alert, Button, Select, Space, Spin, Table, message,
} from 'antd';
import csrfHeaders from '../../../utils/csrfHeaders';
import { captureAnalytics } from '../../../actions/preload';

const failureMessage = (error) => error.response?.data?.error || 'Could not load NextGen mappings. Please try again.';
const destinationKey = (destination) => `${destination.credential_id}:${destination.practice_id}`;

const MappingEditor = ({ destination, isAdmin }) => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [suggested, setSuggested] = useState(false);
  const mounted = useRef(true);
  const base = `/api/v1/nextgen_category_mappings/${destination.credential_id}`;
  const practiceId = destination.practice_id;
  const params = { practice_id: practiceId };

  const load = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const response = await axios.get(base, { params: { practice_id: practiceId } });
      if (!mounted.current) return;
      setData(response.data);
      setDraft({ ...response.data.suggested_mappings, ...response.data.mappings });
      setSuggested(Object.keys(response.data.suggested_mappings).length > 0);
    } catch (err) {
      if (mounted.current) setError(failureMessage(err));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [base, practiceId]);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => { mounted.current = false; };
  }, [load]);

  const suggest = async () => {
    setBusy(true);
    setError('');
    captureAnalytics('nextgen_category_auto_map_requested');
    try {
      const response = await axios.post(`${base}/suggest`, {
        ...params,
        revision: data.revision,
        selected_category_ids: Object.keys(draft).filter((id) => draft[id]),
      }, { headers: csrfHeaders() });
      if (!mounted.current) return;
      setDraft((previous) => ({ ...response.data.suggestions, ...previous }));
      setSuggested(Object.keys(response.data.suggestions).length > 0);
      if (!Object.keys(response.data.suggestions).length) {
        message.info('Auto-map found no confident matches. Select the remaining document types manually.');
      }
    } catch (err) {
      if (mounted.current) setError(failureMessage(err));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError('');
    captureAnalytics('nextgen_category_mappings_save_requested');
    try {
      const mappings = Object.fromEntries(data.categories.map((category) => [category.id, draft[category.id]]));
      const response = await axios.put(base, { ...params, revision: data.revision, mappings }, { headers: csrfHeaders() });
      if (!mounted.current) return;
      setData(response.data);
      setDraft(response.data.mappings);
      setSuggested(false);
      message.success('NextGen document type mappings saved.');
    } catch (err) {
      if (mounted.current) setError(failureMessage(err));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  if (!data) {
    return error ? <Alert type="error" message={error} action={<Button onClick={() => { captureAnalytics('nextgen_category_mappings_retried'); load(); }}>Retry</Button>} /> : <Spin />;
  }
  const types = data.document_types;
  const ready = (id) => types.some((type) => type.id === id && type.automatic);
  const count = data.categories.filter((category) => ready(draft[category.id])).length;
  const locked = !isAdmin || busy || !data.metadata_loaded;
  const columns = [
    { title: 'Medsender category', key: 'name', render: (_, row) => row.ai_name || row.name },
    {
      title: 'NextGen document type',
      key: 'type',
      render: (_, row) => {
        const selected = types.find((type) => type.id === draft[row.id]);
        return (
          <div>
            <Select
              aria-label={`NextGen type for ${row.name}`}
              showSearch
              optionFilterProp="label"
              style={{ width: '100%', minWidth: 200 }}
              disabled={locked}
              value={draft[row.id] || undefined}
              placeholder="Select a document type"
              options={types.map((type) => ({ value: type.id, label: `${type.name} (${type.id.slice(-6)})`, disabled: !type.automatic }))}
              onChange={(value) => {
                setDraft((previous) => ({ ...previous, [row.id]: value }));
                captureAnalytics('nextgen_category_mapping_selected', { document_category_id: row.id });
              }}
            />
            {data.metadata_loaded && draft[row.id] && !ready(draft[row.id]) && (
              <div role="alert" style={{ color: '#b42318' }}>
                {selected?.required_fields?.length
                  ? `Requires values automatic uploads cannot supply: ${selected.required_fields.join(', ')}.`
                  : 'This document type is unavailable. Select another type or contact Medsender to sync metadata.'}
              </div>
            )}
            {selected?.defaults?.length > 0 && <div>{selected.defaults.join('; ')}</div>}
          </div>
        );
      },
    },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {!data.metadata_loaded && <Alert type="warning" showIcon message="NextGen document metadata hasn’t been synced. Contact Medsender to sync your metadata." />}
      {error && <Alert type="error" message={error} />}
      <div>
        {count}
        {' '}
        of
        {' '}
        {data.categories.length}
        {' '}
        enabled categories mapped
      </div>
      <div>Every enabled category needs a document type. Categories enabled later cannot file automatically until mapped here.</div>
      {!data.active && data.metadata_loaded && <Alert type="info" message="Review all selections and save to complete setup. Existing filing settings remain in use until then." />}
      {suggested && <Alert type="info" message="Review the suggested selections before saving. Suggestions have not been applied to uploads." />}
      <Table rowKey="id" columns={columns} dataSource={data.categories} pagination={{ pageSize: 10 }} scroll={{ x: 500 }} size="small" />
      <Space wrap>
        <Button disabled={locked || count === data.categories.length} onClick={suggest}>Auto-map</Button>
        <Button type="primary" disabled={locked || count !== data.categories.length} onClick={save}>Save mappings</Button>
        <Button disabled={busy} onClick={() => { captureAnalytics('nextgen_category_mappings_reloaded'); load(); }}>Reload mappings</Button>
      </Space>
      <div>Types requiring additional user input cannot be selected for automatic filing. Use Send to Chart for those types or contact Medsender.</div>
    </Space>
  );
};

MappingEditor.propTypes = { destination: PropTypes.object.isRequired, isAdmin: PropTypes.bool.isRequired };

const NextgenCategoryMappings = ({ isAdmin }) => {
  const [destinations, setDestinations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [hasNextgen, setHasNextgen] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    axios.get('/api/v1/nextgen_category_mappings').then(({ data }) => {
      if (!active) return;
      setDestinations(data.destinations);
      setHasNextgen(data.has_nextgen);
      if (data.destinations.length === 1) setSelected(destinationKey(data.destinations[0]));
    }).catch((err) => { if (active) setError(failureMessage(err)); });
    return () => { active = false; };
  }, []);
  if (error) return <Alert type="error" message={error} />;
  if (!destinations.length) {
    return hasNextgen
      ? <Alert type="warning" message="NextGen document metadata hasn’t been synced. Contact Medsender to sync your metadata." />
      : null;
  }
  const destination = destinations.find((entry) => destinationKey(entry) === selected);
  return (
    <section style={{
      margin: '16px 0', padding: 16, border: '1px solid #e5e7eb', borderRadius: 8,
    }}
    >
      <h3>NextGen document type mappings</h3>
      {destinations.length > 1 && (
        <Select
          aria-label="NextGen practice"
          placeholder="Select a NextGen practice"
          style={{ width: '100%', marginBottom: 12 }}
          value={selected || undefined}
          options={destinations.map((entry) => ({ value: destinationKey(entry), label: `${entry.name} (integration ${entry.credential_id})` }))}
          onChange={(value) => { setSelected(value); captureAnalytics('nextgen_category_mapping_practice_selected'); }}
        />
      )}
      {destination && <MappingEditor key={selected} destination={destination} isAdmin={isAdmin} />}
    </section>
  );
};

NextgenCategoryMappings.propTypes = { isAdmin: PropTypes.bool.isRequired };
export default NextgenCategoryMappings;
